import React, { PropsWithChildren, useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { ORDER_PRICE_RANGE_MULTIPLIER } from "@shared/features/tradingTerminal/common/constants";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { USD_ALTERNATIVE_PAIRS } from "@shared/features/bots/constants";
import { useClientsData } from "@shared/utils/hooks/misc";

const ExchangesMetadataProvider = ({ children }: PropsWithChildren) => {
  const getExchangesMetadataStatus =
    sharedLib.sharedApi.common.useGetExchangesMetadataQuery(undefined);
  const { data: exchangesMetadata, isSuccess } = getExchangesMetadataStatus;
  const {
    getAccountById,
    getIsMicaAccount,
    isSuccess: isClientsDataSuccess,
  } = useClientsData();

  const getExchangeMetadata = (exchange?: string) => {
    return exchange ? exchangesMetadata?.[exchange] : undefined;
  };

  const getExchangePriceFactorLimits = (exchange?: string) =>
    getExchangeMetadata(exchange)?.limits?.price_factor ?? {
      min: ORDER_PRICE_RANGE_MULTIPLIER,
      max: ORDER_PRICE_RANGE_MULTIPLIER,
    };

  const getAlternativeUsdPairs = (accountId?: number) => {
    const account = getAccountById(accountId);
    const exchangeMetadata = getExchangeMetadata(account?.exchange);
    const pairs = getIsMicaAccount(accountId)
      ? exchangeMetadata?.mica_pairs
      : exchangeMetadata?.pairs;
    return pairs?.filter((pair) => USD_ALTERNATIVE_PAIRS.includes(pair));
  };

  const contextValue = useMemo(
    () => ({
      exchangesMetadata,
      getExchangesMetadataStatus,
      getExchangeMetadata,
      getExchangePriceFactorLimits,
      isSuccess,
      getAlternativeUsdPairs,
    }),
    [exchangesMetadata, isClientsDataSuccess]
  );

  return (
    <ExchangesMetadataContext.Provider value={contextValue}>
      {children}
    </ExchangesMetadataContext.Provider>
  );
};

export default React.memo(ExchangesMetadataProvider);
